/* Build-in hooks */
import { createContext, useContext, useState } from "react";

// Create a context for Preview Player
const PreviewPlayerContext = createContext();

// Custom hook to access the Preview Player context
export const usePreviewPlayer = () => {
  const context = useContext(PreviewPlayerContext);
  if (!context)
    throw new Error(
      "usePreviewPlayer must be used within a PreviewPlayerProvider"
    );
  return context;
};

// Provider component to manage the state and logic for Preview Player
export const PreviewPlayerProvider = ({ children }) => {
  const [previewList, setPreviewList] = useState([]); // List of preview tracks
  const [currentIndex, setCurrentIndex] = useState(0); // Index of the currently playing preview
  const [currentPreview, setCurrentPreview] = useState(null); // Currently playing preview track
  const [isPlaying, setIsPlaying] = useState(false); // Playback state (playing or paused)

  // Function to move to the next track
  const handleNext = () => {
    if (currentIndex < previewList.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  // Function to move to the previous track
  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  // Function to set preview list and start playing
  const playPreview = (previews, index = 0) => {
    setPreviewList(previews);
    setCurrentIndex(index);
    setCurrentPreview(previews[index]);
    setIsPlaying(true);
  };

  // Function to close/stop the preview player
  const closePlayer = () => {
    setIsPlaying(false);
    setCurrentPreview(null);
    setPreviewList([]);
    setCurrentIndex(0);
  };

  return (
    <PreviewPlayerContext.Provider
      value={{
        previewList,
        setPreviewList,
        currentIndex,
        setCurrentIndex,
        currentPreview,
        setCurrentPreview,
        isPlaying,
        setIsPlaying,
        handleNext,
        handlePrev,
        playPreview,
        closePlayer,
      }}
    >
      {children}
    </PreviewPlayerContext.Provider>
  );
};
